import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  configureStore,
  ConfigureStoreOptions,
  isRejected,
  isRejectedWithValue,
  Middleware,
  MiddlewareAPI,
  PayloadAction,
} from '@reduxjs/toolkit';

type SnackbarOptions = Omit<
  Parameters<typeof enqueueSnackbar>[0],
  'title' | 'subTitle' | 'variant'
> & {
  skipAppearance?: boolean;
};

type CustomErrorObject<T = any> = {
  title?: string;
  message?: string;
  snackbarOptions?: SnackbarOptions;
  code?: string;
  status?: number;
  data?: T;
};

const CONDITION_ERROR = 'ConditionError';

// you can pass this function in transformErrorResponse inside of your RTK Query request
export const formCustomRTKQueryError = <T>({
  title,
  message,
  snackbarOptions,
  code,
  status,
  data,
}: CustomErrorObject<T>) => ({
  title,
  message,
  snackbarOptions,
  code,
  status,
  data,
});

const rtkQueryErrorHandler: Middleware =
  (api: MiddlewareAPI) =>
  (next) =>
  (action: PayloadAction<CustomErrorObject>) => {
    try {
      const isQueryRejected = isRejectedWithValue(action) || isRejected(action);
      const snackbarOptions = action?.payload?.snackbarOptions;
      const isConditionError =
        isQueryRejected && action.error.name === CONDITION_ERROR;
      const shouldShowSnackbar =
        isQueryRejected &&
        !snackbarOptions?.skipAppearance &&
        !isConditionError;

      if (shouldShowSnackbar) {
        enqueueSnackbar({
          title: action?.payload?.title || 'Error',
          subTitle: action?.payload?.message || action?.error?.message || '',
          variant: 'error',
          ...snackbarOptions,
        });
      }
      return next(action);
    } catch (error) {
      console.error(error);
    }
  };

export const configureRTQStore = (options: ConfigureStoreOptions) =>
  configureStore({
    ...options,
    middleware: (getDefaultMiddleware) => {
      if (!options?.middleware) {
        return getDefaultMiddleware().concat(rtkQueryErrorHandler);
      }

      if (typeof options.middleware === 'function') {
        return options
          .middleware(getDefaultMiddleware)
          .concat(rtkQueryErrorHandler);
      }

      return [];
    },
  });
